import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  usePatchApiCompaniesCurrentServicemodulesServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import { notify } from '@/lib/notify'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'
import {type ServiceModuleModel, ServiceModuleType} from '@/api/model'

interface Props {
  module: ServiceModuleModel
  onClose: () => void
  modules: ServiceModuleModel[]
}

/**
 * Editor for module types without their own specifics (folder, personal documents,
 * absence, time tracking, calendar metadata …). Saves only the standard
 * fields via the generic endpoint — single-column.
 */
export function EditDefaultServiceModule({ module, onClose, modules }: Props) {
  const { t } = useTranslation('service-modules')
  const standardFields = useStandardServiceModuleFields(module)
  const queryClient = useQueryClient()
  const patch = usePatchApiCompaniesCurrentServicemodulesServiceModuleId()
  const hasChildren = module.type === ServiceModuleType.Folder
      ? modules.some(m => m.parentFolderServiceModuleId === module.id)
      : undefined

  async function handleSave() {
    try {
      await patch.mutateAsync({ serviceModuleId: module.id, data: standardFields.buildBasePayload() })
      await standardFields.saveTranslations()
      await queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() })
      notify.success(t('toasts.saved'))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError'))
    }
  }

  return (
    <EditServiceModuleDialog
      id={module.id}
      moduleName={module.name}
      dialogTitle={t('editDefault.title')}
      onClose={onClose}
      standard={<StandardModuleFields fields={standardFields} />}
      onSave={handleSave}
      isSaving={patch.isPending}
      hasChildren={hasChildren}
    />
  )
}
